import { oc } from "@orpc/contract";
import * as z from "zod";
import { McpTransportSchema } from "../mcp.js";
import {
  DrokMcpAuthModeSchema,
  DrokTemplatePolicySchema,
  DrokTemplateSnapshotSchema,
} from "./templates.js";

const Id = z.string().min(1).max(200);
const Notes = z.string().trim().max(4_000).default("");

export const DrokSnapshotWarningSchema = z.object({
  code: z.enum([
    "mcp_servers",
    "capability_installs",
    "plugin_skills",
    "taught_skills",
    "bot_secrets",
    "webhook_secret",
    "home_files",
    "browser_login",
    "routine_triggers",
  ]),
  message: z.string(),
  count: z.number().int(),
});
export type DrokSnapshotWarningDto = z.infer<typeof DrokSnapshotWarningSchema>;

export const DrokTemplateSummarySchema = z.object({
  id: Id,
  name: z.string(),
  sourceBotId: z.string().nullable(),
  latestVersion: z.number().int(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  activeAssignments: z.number().int(),
  copies: z.number().int(),
});
export type DrokTemplateSummary = z.infer<typeof DrokTemplateSummarySchema>;

export const DrokTemplateVersionSummarySchema = z.object({
  id: Id,
  version: z.number().int(),
  notes: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  routineCount: z.number().int(),
  skillCount: z.number().int(),
  policy: DrokTemplatePolicySchema,
});

export const DrokPublishPickSchema = z.object({
  /** The recurring routines of the source bot to carry. */
  routineIds: z.array(Id).max(50).default([]),
  /** IT's own skills to carry. */
  skillIds: z.array(Id).max(100).default([]),
  mcpServers: z
    .array(z.object({ serverId: Id, authMode: DrokMcpAuthModeSchema }))
    .max(50)
    .default([]),
  policy: DrokTemplatePolicySchema,
  notes: Notes,
});

export const DrokPublishResultSchema = z.object({
  templateId: Id,
  versionId: Id,
  version: z.number().int(),
  warnings: z.array(DrokSnapshotWarningSchema),
});

/** IT-only. None of these is in the employee access map, so they stay default-deny. */
export const drokTemplateContract = {
  list: oc
    .input(
      z.object({ includeArchived: z.boolean().default(false) }).default({ includeArchived: false }),
    )
    .output(z.array(DrokTemplateSummarySchema)),
  get: oc.input(z.object({ templateId: Id })).output(
    z.object({
      template: DrokTemplateSummarySchema,
      versions: z.array(DrokTemplateVersionSummarySchema),
    }),
  ),
  getVersion: oc.input(z.object({ templateId: Id, version: z.number().int().min(1) })).output(
    z.object({
      id: Id,
      version: z.number().int(),
      notes: z.string(),
      createdBy: z.string(),
      createdAt: z.string(),
      snapshot: DrokTemplateSnapshotSchema,
      policy: DrokTemplatePolicySchema,
    }),
  ),
  publish: oc
    .input(DrokPublishPickSchema.extend({ botId: Id, name: z.string().trim().min(1).max(200) }))
    .output(DrokPublishResultSchema),
  publishVersion: oc
    .input(DrokPublishPickSchema.extend({ templateId: Id, botId: Id.optional() }))
    .output(DrokPublishResultSchema),
  archive: oc.input(z.object({ templateId: Id })).output(z.object({ ok: z.literal(true) })),
  sourceBots: oc.output(
    z.array(
      z.object({
        id: Id,
        name: z.string(),
        title: z.string(),
        modelProvider: z.string().nullable(),
        modelId: z.string().nullable(),
        /** The model a copy will actually get: the bot's own, or the deployment default when it has none. */
        effectiveModelProvider: z.string().nullable(),
        effectiveModelId: z.string().nullable(),
        modelIsDefault: z.boolean(),
        routineCount: z.number().int(),
        skillCount: z.number().int(),
      }),
    ),
  ),
  /** IT's own connected Composio apps, for the toolkit picker. */
  connectedToolkits: oc.output(z.array(z.object({ slug: z.string(), name: z.string() }))),
  sourceDetails: oc.input(z.object({ botId: Id })).output(
    z.object({
      bot: z.object({
        id: Id,
        name: z.string(),
        modelProvider: z.string().nullable(),
        modelId: z.string().nullable(),
        effectiveModelProvider: z.string().nullable(),
        effectiveModelId: z.string().nullable(),
        modelIsDefault: z.boolean(),
      }),
      routines: z.array(
        z.object({
          id: Id,
          name: z.string(),
          crons: z.array(z.string()),
          timezone: z.string(),
          active: z.boolean(),
          /** False for a routine with no schedule: it cannot be carried. */
          carryable: z.boolean(),
        }),
      ),
      skills: z.array(
        z.object({
          id: Id,
          name: z.string(),
          description: z.string(),
          fileCount: z.number().int(),
        }),
      ),
      mcpServers: z.array(
        z.object({ id: Id, name: z.string(), transport: McpTransportSchema, enabled: z.boolean() }),
      ),
      warnings: z.array(DrokSnapshotWarningSchema),
    }),
  ),
};
